import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  TrendingUp,
  DollarSign,
  Users,
  Star
} from 'lucide-react';
import { 
  formatNumber, 
  formatCurrency, 
  formatRelativeTime, 
  getAvatarFallback,
  truncateText,
  getMomentumColor
} from '../lib/utils';

export function ProjectCard({ project, featured = false }) {
  const {
    project_id,
    title,
    tagline,
    image_url,
    category,
    creator_name,
    creator_picture,
    momentum_score,
    total_likes,
    total_comments,
    total_money_raised,
    unique_supporters,
    status,
    created_at
  } = project;

  const defaultImage = 'https://images.pexels.com/photos/3912477/pexels-photo-3912477.jpeg';

  return (
    <Link
      to={`/project/${project_id}`}
      className={`card-project block group ${featured ? 'bento-featured' : ''}`}
      data-testid={`project-card-${project_id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden rounded-xl mb-4 aspect-video bg-slate-100">
        <img
          src={image_url || defaultImage}
          alt={title}
          className="w-full h-full object-cover project-card-image"
          loading="lazy"
        />
        
        {/* Momentum Badge */}
        <div className="absolute top-3 right-3 glass-card px-3 py-1.5 flex items-center gap-1.5">
          <TrendingUp className={`w-4 h-4 ${getMomentumColor(momentum_score)}`} />
          <span className={`font-bold ${getMomentumColor(momentum_score)}`}>
            {Math.round(momentum_score)}
          </span>
        </div>

        {/* Smart Pick Badge */}
        {status === 'smart_pick' && (
          <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-lg flex items-center gap-1 text-xs font-medium">
            <Star className="w-3 h-3" />
            Smart Pick
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-3">
        {/* Category */}
        <Badge variant="secondary" className="text-xs capitalize">
          {category}
        </Badge>

        {/* Title & Tagline */}
        <div>
          <h3 className="font-bold text-lg text-slate-900 group-hover:text-orange-600 transition-colors line-clamp-1">
            {title}
          </h3>
          <p className="text-slate-600 text-sm mt-1 line-clamp-2">
            {truncateText(tagline, featured ? 150 : 80)}
          </p>
        </div>

        {/* Creator */}
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={creator_picture} alt={creator_name} />
            <AvatarFallback className="text-xs bg-slate-100">
              {getAvatarFallback(creator_name)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-600">{creator_name}</span>
          <span className="text-slate-400 text-xs">•</span>
          <span className="text-slate-400 text-xs">{formatRelativeTime(created_at)}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
            <Heart className="w-4 h-4" />
            <span>{formatNumber(total_likes)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
            <MessageCircle className="w-4 h-4" />
            <span>{formatNumber(total_comments)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-500 text-sm">
            <Users className="w-4 h-4" />
            <span>{formatNumber(unique_supporters)}</span>
          </div>
          {total_money_raised > 0 && (
            <div className="flex items-center gap-1.5 text-green-600 text-sm ml-auto">
              <DollarSign className="w-4 h-4" />
              <span className="font-medium">{formatCurrency(total_money_raised)}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export function ProjectCardSkeleton() {
  return (
    <div className="card-project">
      <div className="aspect-video bg-slate-200 rounded-xl mb-4 skeleton-shimmer" />
      <div className="space-y-3">
        <div className="w-16 h-5 bg-slate-200 rounded skeleton-shimmer" />
        <div className="space-y-2">
          <div className="w-3/4 h-6 bg-slate-200 rounded skeleton-shimmer" />
          <div className="w-full h-4 bg-slate-200 rounded skeleton-shimmer" />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-slate-200 rounded-full skeleton-shimmer" />
          <div className="w-24 h-4 bg-slate-200 rounded skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
}
